import {computed, Injectable, OnDestroy, signal,} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {merge, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {
  ACCEPTANCE_GROUP_KEY,
  AcceptanceCheckboxConfig,
  CategoryConfig,
  DynamicFormConfig,
  FieldConfig,
  FormSnapshot,
  INITIAL_VALUES_KEY,
} from '../models/form-config.model';

/**
 * DynamicFormService — cuore dell'engine.
 *
 * NON è providedIn 'root': viene fornito nel providers[] di DynamicFormComponent,
 * garantendo un'istanza isolata per ogni form nella pagina.
 *
 * Responsabilità:
 * - Costruisce il FormGroup reattivo a partire dalla config
 * - Mantiene lo snapshot dei valori correnti tramite Signals
 * - Sincronizza visibilità e validatori dei campi condizionali
 * - Gestisce lo stato aperto/chiuso delle categorie accordion
 * - Espone helper per validità per categoria, navigazione tra sezioni
 * - Gestisce un FormGroup dedicato per le `acceptances` globali (`__acceptances__`)
 * - Espone `areAcceptancesValid` (computed Signal) per abilitare il submit
 * - `getFormValue()` include le accettazioni nello snapshot
 */
@Injectable()
export class DynamicFormService implements OnDestroy {

  // ── Stato privato ────────────────────────────────────────────────────────
  private readonly _config = signal<DynamicFormConfig | null>(null);
  private readonly _snapshot = signal<FormSnapshot>({});
  /** Key delle categorie attualmente aperte. Di base contiene tutte le categorie visibili. */
  private readonly _openKeys = signal<ReadonlySet<string>>(new Set());
  /** Ultima categoria aperta (dall'utente, o la prima all'avvio): usata solo per il focus. */
  private readonly _lastOpenedKey = signal<string | null>(null);
  private readonly _stateTrigger = signal(0);

  private _initialSnapshot: FormSnapshot = {};

  private readonly _init$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  // ── Segnali pubblici (readonly) ──────────────────────────────────────────

  /** True se l'utente ha modificato almeno un campo (esclude prefill). */
  readonly isDirty = signal(false);

  /** Config corrente del form. */
  readonly config = computed(() => this._config());

  /**
   * Key dell'ultima categoria aperta (dall'utente, o la prima categoria
   * all'avvio del form). Usata solo per spostare il focus sul suo primo campo.
   */
  readonly lastOpenedKey = computed(() => this._lastOpenedKey());

  /**
   * Lista delle categorie visibili, ricalcolata automaticamente
   * a ogni cambio del form snapshot.
   */
  readonly visibleCategories = computed(() => {
    const cfg = this._config();
    const snapshot = this._snapshot();
    if (!cfg) return [];
    return cfg.categories.filter(c => !c.showIf || c.showIf(snapshot));
  });

  /** Snapshot corrente dei valori del form (getRawValue — include disabilitati).
   * Accessibile come Signal per computed derivati nei componenti figli.
   */
  readonly snapshot = computed(() => this._snapshot());


  /** Signal che emette true se il form è valido. */
  readonly isValid = signal(false);


  /**
   * True se tutte le checkbox di accettazione obbligatorie sono spuntate.
   * Usato da DynamicFormComponent per disabilitare il submit.
   * È true anche se non ci sono accettazioni configurate.
   */
  readonly areAcceptancesValid = computed(() => {
    const cfg = this._config();
    if (!cfg?.acceptances?.length) return true;
    const acceptGroup = this.rootGroup()?.get(ACCEPTANCE_GROUP_KEY) as FormGroup | null;
    if (!acceptGroup) return true;
    return acceptGroup.valid;
  });

  // ── FormGroup root ───────────────────────────────────────────────────────

  /** FormGroup radice: un FormGroup figlio per ogni categoria. */
  readonly rootGroup = signal<FormGroup | null>(null);

  // ── Init ─────────────────────────────────────────────────────────────────

  /**
   * Inizializza il servizio con la config e i valori opzionali di prefill.
   * Va chiamato una sola volta, nel ngOnInit di DynamicFormComponent.
   *
   * @param config    Configurazione dichiarativa del form
   * @param prefill   Valori iniziali (es. dati dal backend o draft salvato)
   */
  init(config: DynamicFormConfig, prefill?: FormSnapshot): void {
    this._init$.next(); // Cancella sottoscrizioni precedenti se presente un re-init
    this._config.set(config);
    const group = this.buildRootGroup(config, prefill);
    this.rootGroup.set(group);

    // Snapshot iniziale comprensivo di prefill
    const raw = group.getRawValue() as FormSnapshot;
    this._initialSnapshot = raw;

    const tempSnapshot: FormSnapshot = {
      ...raw,
      [INITIAL_VALUES_KEY]: raw
    };

    // Sincronizziamo i controlli condizionali una prima volta
    this.syncConditionalControls(tempSnapshot);

    // Aggiungiamo metadati sulla validità delle categorie (escludendo le accettazioni)
    const categoriesValid = this.checkCategoriesValidity(group, config, tempSnapshot);

    const finalSnapshot: FormSnapshot = {
      ...tempSnapshot,
      ['__allCategoriesValid__' as any]: categoriesValid
    };

    // Seconda sincronizzazione per le accettazioni con il valore di __allCategoriesValid__ aggiornato
    this.syncConditionalControls(finalSnapshot);

    this._snapshot.set(finalSnapshot);

    // Reazione ai cambiamenti del form
    this.listenToChanges();

    // Di base tutte le categorie visibili sono aperte; l'utente può chiudere
    // o riaprire ciascuna singolarmente (nessun "espandi/chiudi tutto").
    const visible = this.visibleCategories();
    this._openKeys.set(new Set(visible.map(c => c.key)));
    if (visible.length > 0) {
      this._lastOpenedKey.set(visible[0].key);
    }

    // Forza un aggiornamento della validità dopo la sincronizzazione iniziale
    this.isValid.set(group.valid);
    this.notifyStateChange();

    // Ri-triggera la validazione asincrona (es. unicità email) DOPO che
    // listenToChanges() è attivo, così che — in modifica con valori prefillati —
    // la sua risoluzione emetta e aggiorni l'indicatore ✓ della sezione.
    setTimeout(() => {
      this.retriggerAsyncValidation();
    });
  }

  /**
   * Riesegue i validatori asincroni dei controlli abilitati con emitEvent:true,
   * così che la loro risoluzione emetta statusChanges e i segnali reattivi
   * (validità delle sezioni) si aggiornino anche con valori prefillati.
   */
  private retriggerAsyncValidation(): void {
    const cfg = this._config();
    const group = this.rootGroup();
    if (!cfg || !group) return;

    for (const cat of cfg.categories) {
      for (const field of cat.fields) {
        if (!field.asyncValidators?.length) continue;
        const ctrl = group.get(`${cat.key}.${field.key}`) as FormControl | null;
        if (ctrl && ctrl.enabled) {
          ctrl.updateValueAndValidity({emitEvent: true});
        }
      }
    }
  }

  // ── Costruzione FormGroup ────────────────────────────────────────────────

  private buildRootGroup(
    cfg: DynamicFormConfig,
    prefill?: FormSnapshot,
  ): FormGroup {
    const groups: Record<string, FormGroup> = {};

    // Categorie
    for (const cat of cfg.categories) {
      const catPrefill = prefill?.[cat.key] as Record<string, unknown> | undefined;
      groups[cat.key] = this.buildCategoryGroup(cat, catPrefill);
    }

    // Gruppo accettazioni globali (fuori accordion)
    if (cfg.acceptances?.length) {
      const acceptPrefill = prefill?.[ACCEPTANCE_GROUP_KEY] as Record<string, unknown> | undefined;
      groups[ACCEPTANCE_GROUP_KEY] = this.buildAcceptanceGroup(
        cfg.acceptances,
        acceptPrefill,
      );
    }

    return new FormGroup(groups);
  }

  private buildCategoryGroup(
    cat: CategoryConfig,
    prefill?: Record<string, unknown>,
  ): FormGroup {
    const controls: Record<string, FormControl> = {};

    for (const field of cat.fields) {
      // I campi di tipo 'acceptance' dentro una categoria hanno
      // il loro FormControl come boolean (true/false)
      if (field.type === 'acceptance' && field.acceptance) {
        const acc = field.acceptance;
        controls[field.key] = this.buildAcceptanceControl(acc, prefill?.[field.key] as boolean | undefined);
      }
      else {
        controls[field.key] = new FormControl(
          prefill?.[field.key] ?? field.defaultValue ?? null,
          {
            validators: field.validators ?? [],
            asyncValidators: field.asyncValidators ?? [],
          },
        );
      }
    }
    return new FormGroup(controls, cat.validators?.length ? { validators: cat.validators } : null);
  }

  /**
   * Costruisce il FormGroup per le accettazioni globali (fuori accordion).
   * Chiave riservata: ACCEPTANCE_GROUP_KEY.
   */
  private buildAcceptanceGroup(
    acceptances: AcceptanceCheckboxConfig[],
    prefill?: Record<string, unknown>,
  ): FormGroup {
    const controls: Record<string, FormControl> = {};
    for (const acc of acceptances) {
      controls[acc.key] = this.buildAcceptanceControl(acc, prefill?.[acc.key] as boolean | undefined);
    }
    return new FormGroup(controls);
  }

  private buildAcceptanceControl(
    acc: AcceptanceCheckboxConfig,
    prefillValue?: boolean,
  ): FormControl {
    const initial = prefillValue ?? acc.defaultChecked ?? false;
    // required: true (default) → usa Validators.requiredTrue
    // valida solo se il valore è esattamente `true`
    const validators = (acc.required !== false)
      ? [Validators.requiredTrue]
      : [];
    return new FormControl(initial, validators);
  }

  // ── Reattività ───────────────────────────────────────────────────────────

  private listenToChanges(): void {
    const group = this.rootGroup();
    if (!group) return;

    merge(group.valueChanges, group.statusChanges)
    .pipe(
      takeUntil(this.destroy$),
      takeUntil(this._init$)
    )
    .subscribe(() => {
      // getRawValue include i controlli disabilitati → snapshot completo
      const raw = group.getRawValue() as FormSnapshot;

      const tempSnapshot: FormSnapshot = {
        ...raw,
        [INITIAL_VALUES_KEY]: this._initialSnapshot,
      };

      // 1. Sincronizziamo lo stato dei controlli condizionali basandoci sui nuovi valori raw
      this.syncConditionalControls(tempSnapshot);

      // 2. Ricalcoliamo la validità basandoci sui controlli appena abilitati/disabilitati
      const categoriesValid = this.checkCategoriesValidity(group, this._config(), tempSnapshot);

      const finalSnapshot: FormSnapshot = {
        ...tempSnapshot,
        ['__allCategoriesValid__' as any]: categoriesValid
      };

      // 3. Seconda sincronizzazione per le accettazioni con il valore di __allCategoriesValid__ aggiornato
      this.syncConditionalControls(finalSnapshot);

      this._snapshot.set(finalSnapshot);

      // Calcola se il form è effettivamente dirty confrontando il valore corrente con quello iniziale
      const isDirty = JSON.stringify(raw) !== JSON.stringify(this._initialSnapshot);
      this.isDirty.set(isDirty);

      this.isValid.set(group.valid);
      this.notifyStateChange();
    });
  }

  /** Incrementa il trigger di stato per notificare i signal dipendenti (es. validità/touched). */
  notifyStateChange(): void {
    this._stateTrigger.update(v => v + 1);
  }

  /**
   * Sincronizza lo stato di ogni controllo in base alle funzioni
   * showIf / disabledIf della config, usando lo snapshot corrente.
   *
   * Regole:
   * - Campo nascosto → disable() + clearValidators() (escluso da validità e valore)
   * - Campo disabilitato → disable() + mantiene validatori
   * - Campo visibile → enable() + ripristina validatori dalla config
   *
   * emitEvent: false evita loop ricorsivi.
   */
  private syncConditionalControls(snapshot: FormSnapshot = this._snapshot()): void {
    const cfg = this._config();
    const group = this.rootGroup();
    if (!cfg || !group) return;

    // 1. Sincronizza campi nelle categorie
    for (const cat of cfg.categories) {
      const catGroup = this.getCategoryGroup(cat.key);
      if (!catGroup) continue;

      const catVisible = !cat.showIf || cat.showIf(snapshot);

      if (!catVisible) {
        // enable/disable innescano updateValueAndValidity sui figli (e quindi
        // i loro validatori async): li chiamiamo solo in transizione di stato.
        if (catGroup.enabled) catGroup.disable({emitEvent: false});
        catGroup.clearValidators(); // Rimuove i validatori cross-field se nascosta
      }
      else {
        if (catGroup.disabled) catGroup.enable({emitEvent: false});
        catGroup.setValidators(cat.validators ?? []); // Ripristina i validatori cross-field
      }

      for (const field of cat.fields) {
        const ctrl = catGroup.get(field.key) as FormControl;
        const fieldVisible = catVisible && (!field.showIf || field.showIf(snapshot));
        const fieldDisabledByFn = field.disabledIf?.(snapshot) ?? false;

        this.syncSingleControl(ctrl, fieldVisible, fieldDisabledByFn, field);

        // Per i campi con validatori asincroni NON rieseguiamo qui la validazione:
        // ri-triggerarla con emitEvent:false annullerebbe la validazione async in
        // corso (avviata dall'input utente) e la sua risoluzione sarebbe silenziosa,
        // impedendo l'aggiornamento dell'indicatore di validità della sezione (✓).
        // La loro validazione è gestita dall'input utente, dalle transizioni di
        // visibilità e da retriggerAsyncValidation() all'init (valori prefillati).
        const hasAsync = (field.asyncValidators?.length ?? 0) > 0;
        if (!hasAsync) {
          ctrl.updateValueAndValidity({emitEvent: false});
        }
      }
      catGroup.updateValueAndValidity({emitEvent: false});

      // ── NUOVO: esegui side effects della categoria ──────────────────────
      if (cat.sideEffects && catVisible) {
        cat.sideEffects(snapshot, catGroup);
      }
      // ───────────────────────────────────────────────────────────────────
    }

    // 2. Sincronizza accettazioni globali (fuori accordion)
    if (cfg.acceptances?.length) {
      const acceptGroup = group.get(ACCEPTANCE_GROUP_KEY) as FormGroup;
      for (const acc of cfg.acceptances) {
        const ctrl = acceptGroup.get(acc.key) as FormControl;
        const accVisible = !acc.showIf || acc.showIf(snapshot);

        if (!accVisible) {
          ctrl.disable({emitEvent: false});
          ctrl.clearValidators();
        }
        else {
          ctrl.enable({emitEvent: false});
          ctrl.setValidators((acc.required !== false) ? [Validators.requiredTrue] : []);
        }
        ctrl.updateValueAndValidity({emitEvent: false});
      }
      acceptGroup.updateValueAndValidity({emitEvent: false});
    }
  }

  /**
   * Logica comune per abilitare/disabilitare un controllo e i suoi validatori.
   */
  private syncSingleControl(
    ctrl: FormControl,
    visible: boolean,
    disabledByFn: boolean,
    config: FieldConfig,
  ): void {
    // enable()/disable() innescano updateValueAndValidity (e quindi i validatori
    // async del controllo): vanno chiamati solo in transizione di stato, così da
    // non ri-triggerare/silenziare la validazione async ad ogni cambiamento.
    if (!visible) {
      if (ctrl.enabled) ctrl.disable({emitEvent: false});
      // I campi acceptance dentro categoria mantengono Validators.requiredTrue
      // solo se visibili; quando nascosti vengono rimossi come gli altri
      if (config.type !== 'acceptance') {
        ctrl.clearValidators();
        ctrl.clearAsyncValidators();
      }
      else {
        ctrl.clearValidators();
      }
    }
    else if (disabledByFn) {
      if (ctrl.enabled) ctrl.disable({emitEvent: false});
      if (config.type === 'acceptance' && config.acceptance) {
        ctrl.setValidators(
          (config.acceptance.required !== false) ? [Validators.requiredTrue] : [],
        );
      }
      else {
        ctrl.setValidators(config.validators ?? []);
        ctrl.setAsyncValidators(config.asyncValidators ?? []);
      }
    }
    else {
      if (ctrl.disabled) ctrl.enable({emitEvent: false});
      if (config.type === 'acceptance' && config.acceptance) {
        ctrl.setValidators(
          (config.acceptance.required !== false) ? [Validators.requiredTrue] : [],
        );
      }
      else {
        ctrl.setValidators(config.validators ?? []);
        ctrl.setAsyncValidators(config.asyncValidators ?? []);
      }
    }
  }

  // ── Accordion ────────────────────────────────────────────────────────────

  /** True se la categoria è aperta. Di base tutte le categorie sono aperte. */
  isCategoryOpen(key: string): boolean {
    return this._openKeys().has(key);
  }

  /** Apre una categoria specifica. Le altre restano nel loro stato corrente. */
  openCategory(key: string): void {
    if (this._openKeys().has(key)) return;
    const next = new Set(this._openKeys());
    next.add(key);
    this._openKeys.set(next);
    this._lastOpenedKey.set(key);
  }

  /** Chiude una categoria specifica. Le altre restano nel loro stato corrente. */
  closeCategory(key: string): void {
    if (!this._openKeys().has(key)) return;
    const next = new Set(this._openKeys());
    next.delete(key);
    this._openKeys.set(next);
  }

  /** Alterna liberamente lo stato aperto/chiuso di una singola categoria. */
  toggleCategory(key: string): void {
    if (this._openKeys().has(key)) {
      this.closeCategory(key);
    }
    else {
      this.openCategory(key);
    }
  }

  /**
   * Marca la categoria come touched (mostra errori), poi apre la successiva.
   * Ritorna la key della categoria successiva, o null se non esiste.
   */
  continueToNext(currentKey: string): string | null {
    const visible = this.visibleCategories();
    const idx = visible.findIndex(c => c.key === currentKey);
    const next = visible[idx + 1];
    if (next) {
      this.openCategory(next.key);
      return next.key;
    }
    return null;
  }

  // ── Helper pubblici ──────────────────────────────────────────────────────

  /** Ritorna il valore completo del form (compresi i campi disabilitati). */
  getFormValue(): FormSnapshot {
    return this.rootGroup()!.getRawValue() as FormSnapshot;
  }

  /** True se il FormGroup della categoria specificata è valido. */
  isCategoryValid(key: string): boolean {
    this._snapshot(); // Reattività al valore
    this._stateTrigger(); // Reattività allo stato
    return this.getCategoryGroup(key)?.valid ?? false;
  }

  /** True se il FormGroup della categoria specificata è in attesa (es. validazione asincrona in corso). */
  isCategoryPending(key: string): boolean {
    this._snapshot();
    this._stateTrigger();
    return this.getCategoryGroup(key)?.pending ?? false;
  }

  /** True se il FormGroup della categoria specificata ha degli errori (inclusi eventuali gruppi annidati). */
  hasCategoryError(key: string): boolean {
    this._snapshot(); // Reattività al valore
    this._stateTrigger(); // Reattività allo stato
    const group = this.getCategoryGroup(key);
    if (!group) return false;

    // La categoria è in errore se è invalida
    return group.invalid;
  }

  /** Marca tutti i controlli della categoria come touched (mostra errori). */
  markCategoryTouched(key: string): void {
    const group = this.getCategoryGroup(key);
    if (group) {
      group.markAllAsTouched();
      this.notifyStateChange();
    }
  }

  /**
   * Marca tutte le checkbox di accettazione come touched
   * per mostrare il messaggio di errore se non spuntate al submit.
   */
  markAcceptancesTouched(): void {
    const acceptGroup = this.rootGroup()?.get(ACCEPTANCE_GROUP_KEY) as FormGroup | null;
    acceptGroup?.markAllAsTouched();
  }

  /** Resetta il flag isDirty (da chiamare dopo un submit o salvataggio). */
  resetDirty(): void {
    const group = this.rootGroup();
    if (group) {
      this._initialSnapshot = group.getRawValue() as FormSnapshot;
    }
    this.isDirty.set(false);
  }

  private checkCategoriesValidity(
    group: FormGroup,
    config: DynamicFormConfig | null,
    snapshot: FormSnapshot = this._snapshot(),
  ): boolean {
    if (!config) return true;
    return config.categories.every(cat => {
      const catIsVisibile = !cat.showIf || cat.showIf(snapshot);

      if (!catIsVisibile) return true;

      const catGroup = group.get(cat.key);
      return !catGroup || catGroup.valid;
    });
  }

  /** Accede a un singolo FormControl tramite categoria e campo. */
  getControl(categoryKey: string, fieldKey: string): FormControl {
    return this.rootGroup()!.get(`${categoryKey}.${fieldKey}`) as FormControl;
  }

  /** Accede al FormGroup di una categoria. */
  getCategoryGroup(key: string): FormGroup {
    return this.rootGroup()!.get(key) as FormGroup;
  }

  /**
   * Accede al FormControl di una singola accettazione globale.
   * @param key  La key dell'AcceptanceCheckboxConfig
   */
  getAcceptanceControl(key: string): FormControl {
    return this.rootGroup()!.get(`${ACCEPTANCE_GROUP_KEY}.${key}`) as FormControl;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
